// packages/shared-components/src/components/DecorativeLight/DecorativeLight.tsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import type { DecorativeLightProps, CustomLightingInfo } from "./types";
import "./DecorativeLight.css";

const DEFAULT_LIGHTING: CustomLightingInfo = {
  R: 0,
  G: 0,
  B: 0,
  isCustom: false,
};

const GRADIENT_TRANSITION_MS = 180;

/** decorative-light 左右 padding 16×2 */
const FIT_CONTAINER_LIGHT_HORIZONTAL_PADDING = 32;
/** decorative-led-grid 左右 padding 8×2（border-box 下需计入外层宽度） */
const FIT_CONTAINER_GRID_HORIZONTAL_PADDING = 16;
const FIT_CONTAINER_HORIZONTAL_CHROME =
  FIT_CONTAINER_LIGHT_HORIZONTAL_PADDING +
  FIT_CONTAINER_GRID_HORIZONTAL_PADDING;

function applyBrightness(
  color: { R: number; G: number; B: number },
  brightness: number,
  showBrightness: boolean
) {
  if (!showBrightness || brightness >= 100) {
    return color;
  }

  const brightnessFactor = brightness / 100;
  return {
    R: Math.round(color.R * brightnessFactor),
    G: Math.round(color.G * brightnessFactor),
    B: Math.round(color.B * brightnessFactor),
  };
}

function toRgbString(color: { R: number; G: number; B: number }) {
  const hasColor = color.R > 0 || color.G > 0 || color.B > 0;
  return hasColor
    ? `rgb(${color.R}, ${color.G}, ${color.B})`
    : "rgb(42, 42, 42)";
}

function isDecorativeLightingGrid(
  data: CustomLightingInfo[][] | undefined
): data is CustomLightingInfo[][] {
  return Array.isArray(data) && data.every((row) => Array.isArray(row));
}

function buildRowGradient(
  rowData: CustomLightingInfo[],
  brightness: number,
  showBrightness: boolean
) {
  if (rowData.length === 0) {
    return "rgb(42, 42, 42)";
  }

  const colors = rowData.map((led) =>
    toRgbString(applyBrightness(led, brightness, showBrightness))
  );

  if (colors.length === 1) {
    return colors[0];
  }

  const stops = colors.map((color, index) => {
    const position = (index / (colors.length - 1)) * 100;
    return `${color} ${position}%`;
  });

  return `linear-gradient(to right, ${stops.join(", ")})`;
}

interface GradientRowProps {
  rowData: CustomLightingInfo[];
  width: number;
  height: number;
  brightness: number;
  showBrightness: boolean;
}

const GradientRow: React.FC<GradientRowProps> = ({
  rowData,
  width,
  height,
  brightness,
  showBrightness,
}) => {
  const gradient = useMemo(
    () => buildRowGradient(rowData, brightness, showBrightness),
    [rowData, brightness, showBrightness]
  );
  const previousGradientRef = useRef(gradient);
  const [previousGradient, setPreviousGradient] = React.useState<string | null>(
    null
  );

  useEffect(() => {
    if (gradient === previousGradientRef.current) {
      return;
    }

    setPreviousGradient(previousGradientRef.current);
    previousGradientRef.current = gradient;

    const timer = window.setTimeout(() => {
      setPreviousGradient(null);
    }, GRADIENT_TRANSITION_MS);

    return () => window.clearTimeout(timer);
  }, [gradient]);

  return (
    <div className="decorative-led-gradient-wrap" style={{ width, height }}>
      {previousGradient ? (
        <div
          className="decorative-led-gradient decorative-led-gradient--previous"
          style={{ background: previousGradient }}
        />
      ) : null}
      <div
        className={`decorative-led-gradient decorative-led-gradient--current${
          previousGradient ? " decorative-led-gradient--animating" : ""
        }`}
        style={{ background: gradient }}
      />
    </div>
  );
};

export const DecorativeLight: React.FC<DecorativeLightProps> = ({
  config,
  lightingData,
  brightness = 100,
  showBrightness = true,
  onLedClick,
  className = "",
  ledWidth = 400,
  ledHeight = 12,
  ledGap = 0,
  ledSize,
  fitWithinContainer = false,
}) => {
  const { rows, cols } = config;
  const rootRef = useRef<HTMLDivElement>(null);
  const [parentWidth, setParentWidth] = useState<number | null>(null);

  const rawDisplayData = isDecorativeLightingGrid(lightingData)
    ? lightingData
    : Array(rows)
        .fill(null)
        .map(() => Array(cols).fill(DEFAULT_LIGHTING));

  const useFixedLedSize = Number(ledSize) > 0;
  const fixedLedSize = useFixedLedSize ? Number(ledSize) : 0;
  const fixedLedHeight =
    useFixedLedSize && Number(ledHeight) > 0 ? Number(ledHeight) : fixedLedSize;
  const useResponsiveFixedStrip = useFixedLedSize && fitWithinContainer;
  /** 自适应模式下严格按 config.cols 布局，避免 SDK 多回灯珠撑破外层 */
  const layoutCols = useResponsiveFixedStrip
    ? cols
    : Math.max(cols, rawDisplayData[0]?.length ?? 0);

  const displayData = rawDisplayData.map((row) =>
    row.slice(0, layoutCols).concat(
      Array(Math.max(0, layoutCols - row.length))
        .fill(null)
        .map(() => DEFAULT_LIGHTING)
    )
  );

  const naturalLedContentWidth = useFixedLedSize
    ? layoutCols * fixedLedSize + Math.max(0, layoutCols - 1) * ledGap
    : ledWidth;
  const naturalGridWidth =
    naturalLedContentWidth + FIT_CONTAINER_GRID_HORIZONTAL_PADDING;
  const naturalOuterWidth =
    naturalGridWidth + FIT_CONTAINER_LIGHT_HORIZONTAL_PADDING;
  const stripWidth = naturalLedContentWidth;

  const contentBudget =
    parentWidth != null
      ? Math.max(0, parentWidth - FIT_CONTAINER_HORIZONTAL_CHROME)
      : null;
  const resolvedLedContentWidth =
    contentBudget != null
      ? Math.min(naturalLedContentWidth, contentBudget)
      : naturalLedContentWidth;
  const resolvedGridWidth =
    resolvedLedContentWidth + FIT_CONTAINER_GRID_HORIZONTAL_PADDING;
  const shrinkLeds =
    useResponsiveFixedStrip &&
    contentBudget != null &&
    naturalLedContentWidth > contentBudget;
  const resolvedOuterWidth =
    parentWidth != null
      ? Math.min(naturalOuterWidth, parentWidth)
      : naturalOuterWidth;

  const useGradientMode = !onLedClick;

  useEffect(() => {
    if (!useResponsiveFixedStrip) {
      setParentWidth(null);
      return;
    }

    const node = rootRef.current?.parentElement;
    if (!node) return;

    const update = () => {
      setParentWidth(node.getBoundingClientRect().width);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [useResponsiveFixedStrip, layoutCols, fixedLedSize, ledGap]);

  const renderLed = (row: number, col: number) => {
    const ledData = displayData[row]?.[col] || DEFAULT_LIGHTING;
    const { R, G, B } = applyBrightness(ledData, brightness, showBrightness);
    const hasColor = ledData.R > 0 || ledData.G > 0 || ledData.B > 0;
    const backgroundColor = toRgbString({ R, G, B });

    const cellWidth = shrinkLeds
      ? undefined
      : useFixedLedSize
        ? fixedLedSize
        : `calc(${ledWidth}px / ${layoutCols} - ${ledGap}px)`;
    const cellHeight = useFixedLedSize ? fixedLedHeight : ledHeight;

    return (
      <div
        key={`${row}-${col}`}
        className={`decorative-led ${hasColor ? "active" : ""}`}
        style={{
          height: cellHeight,
          width: cellWidth,
          maxWidth: shrinkLeds ? fixedLedSize : undefined,
          flex: shrinkLeds ? "1 1 0" : undefined,
          minWidth: shrinkLeds ? 0 : undefined,
          flexShrink: shrinkLeds ? 1 : 0,
          backgroundColor,
          marginRight: col < layoutCols - 1 ? ledGap : 0,
        }}
        onClick={(e) => onLedClick?.(row, col, e)}
        title={`[${row},${col}] RGB(${ledData.R}, ${ledData.G}, ${ledData.B})`}
      />
    );
  };

  const renderRow = (row: number) => {
    const rowData =
      displayData[row]?.slice(0, layoutCols) ||
      Array(layoutCols).fill(DEFAULT_LIGHTING);

    const gradientWidth = useResponsiveFixedStrip
      ? resolvedLedContentWidth
      : useFixedLedSize
        ? naturalLedContentWidth
        : ledWidth;
    const gradientHeight = useFixedLedSize ? fixedLedHeight : ledHeight;

    if (useGradientMode) {
      return (
        <GradientRow
          key={row}
          rowData={rowData}
          width={gradientWidth}
          height={gradientHeight}
          brightness={brightness}
          showBrightness={showBrightness}
        />
      );
    }

    if (useResponsiveFixedStrip) {
      return (
        <div
          className="decorative-led-row"
          style={{
            display: "flex",
            alignItems: "center",
            width: resolvedLedContentWidth,
            maxWidth: "100%",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
          key={row}
        >
          {Array(layoutCols)
            .fill(null)
            .map((_, col) => renderLed(row, col))}
        </div>
      );
    }

    return (
      <div
        className="decorative-led-row"
        style={ledGap > 0 ? { gap: ledGap } : undefined}
        key={row}
      >
        {Array(layoutCols)
          .fill(null)
          .map((_, col) => renderLed(row, col))}
      </div>
    );
  };

  const renderLeds = () => (
    <div
      className="decorative-led-strip"
      style={
        useResponsiveFixedStrip
          ? {
              width: resolvedLedContentWidth,
              maxWidth: "100%",
              overflow: "hidden",
            }
          : { width: stripWidth }
      }
    >
      {Array(rows)
        .fill(null)
        .map((_, row) => renderRow(row))}
    </div>
  );

  if (useResponsiveFixedStrip) {
    return (
      <div
        ref={rootRef}
        className={`decorative-light decorative-light--fit-container ${className}`.trim()}
        style={{
          width: resolvedOuterWidth,
          maxWidth: "100%",
        }}
      >
        <div
          className="decorative-led-grid"
          style={{
            width: resolvedGridWidth,
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          {renderLeds()}
        </div>
      </div>
    );
  }

  return <div className={`decorative-light ${className}`}>{renderLeds()}</div>;
};
